import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

const BrandRow = ({ brands, direction, isPaused, rowRef }) => {
  const copies = Math.max(6, brands.length * 2);
  const items = Array.from({ length: copies }).flatMap((_, ci) =>
    brands.map((brand, i) => ({ ...brand, _key: `${ci}-${i}` }))
  );

  return (
    <div className="overflow-hidden w-full">
      <div
        ref={rowRef}
        className="flex gap-4 items-center"
        style={{ width: 'max-content', willChange: 'transform' }}
      >
        {items.map((brand) => (
          <Link
            key={brand._key}
            to={`/brands?brand=${encodeURIComponent(brand.name)}`}
            className="flex-shrink-0 flex items-center justify-center h-16 px-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            style={{ minWidth: '120px' }}
          >
            {brand.logo ? (
              <img
                src={brand.logo}
                alt={brand.name}
                className="h-10 w-auto object-contain transition-all"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <span
              className="text-sm font-semibold text-gray-700"
              style={{ display: brand.logo ? 'none' : 'block' }}
            >
              {brand.name}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const BrandCarousel = () => {
  const [brands, setBrands] = useState([]);
  const [isDesktopPaused, setIsDesktopPaused] = useState(false);
  const [isMobilePaused, setIsMobilePaused] = useState(false);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const row3Ref = useRef(null);
  const row4Ref = useRef(null);
  const mobRowRef = useRef(null);
  const touchTimeoutRef = useRef(null);
  const positionsRef = useRef([0, 0, 0, 0, 0]);
  const animationRef = useRef(null);
  const speed = 0.4;

  // Clean up mobile touch timeout on unmount
  useEffect(() => {
    return () => {
      if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/products/brands`)
      .then((res) => res.json())
      .then((data) => {
        const filtered = (data || []).filter(
          (b) => b.name?.toLowerCase() !== 'uncategorized'
        );
        setBrands(filtered);
      })
      .catch(() => {});
  }, []);

  // Split brands into 4 rows evenly
  const rows = [[], [], [], []];
  brands.forEach((brand, i) => rows[i % 4].push(brand));
  rows[4] = brands; // full array for mobile single-row ticker

  const rowRefs = [row1Ref, row2Ref, row3Ref, row4Ref, mobRowRef];
  const directions = [1, -1, 1, -1, 1]; // 1 = left, -1 = right

  useEffect(() => {
    if (brands.length === 0) return;

    const animate = () => {
      rowRefs.forEach((ref, i) => {
        const track = ref.current;
        if (!track) return;
        const rowBrandCount = rows[i].length;
        if (rowBrandCount === 0) return;
        const copies = Math.max(6, rowBrandCount * 2);
        const singleCopyWidth = track.scrollWidth / copies;

        const paused = i < 4 ? isDesktopPaused : isMobilePaused;
        if (!paused) {
          positionsRef.current[i] += speed * directions[i];
          if (positionsRef.current[i] >= singleCopyWidth) {
            positionsRef.current[i] -= singleCopyWidth;
          }
          if (positionsRef.current[i] < 0) {
            positionsRef.current[i] += singleCopyWidth;
          }
        }
        track.style.transform = `translateX(-${positionsRef.current[i]}px)`;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands, isDesktopPaused, isMobilePaused]);

  const handlePrev = () => {
    rowRefs.forEach((ref, i) => {
      const track = ref.current;
      if (!track) return;
      const rowBrandCount = rows[i].length;
      if (rowBrandCount === 0) return;
      const copies = Math.max(6, rowBrandCount * 2);
      const itemWidth = track.scrollWidth / (copies * rowBrandCount);
      positionsRef.current[i] -= itemWidth * 2;
      const singleCopyWidth = track.scrollWidth / copies;
      if (positionsRef.current[i] < 0) {
        positionsRef.current[i] += singleCopyWidth;
      }
    });
  };

  const handleNext = () => {
    rowRefs.forEach((ref, i) => {
      const track = ref.current;
      if (!track) return;
      const rowBrandCount = rows[i].length;
      if (rowBrandCount === 0) return;
      const copies = Math.max(6, rowBrandCount * 2);
      const itemWidth = track.scrollWidth / (copies * rowBrandCount);
      positionsRef.current[i] += itemWidth * 2;
      const singleCopyWidth = track.scrollWidth / copies;
      if (positionsRef.current[i] >= singleCopyWidth) {
        positionsRef.current[i] -= singleCopyWidth;
      }
    });
  };

  if (brands.length === 0) return null;

  return (
    <section className="py-12 border-t">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-xl font-bold mb-8 text-center">Featured Brands</h2>

        {/* Desktop: 4-row auto-scrolling ticker (hidden below md) */}
        <div className="hidden md:block">
          {/* Carousel wrapper */}
          <div
            className="relative"
            onMouseEnter={() => setIsDesktopPaused(true)}
            onMouseLeave={() => setIsDesktopPaused(false)}
          >
            {/* Fade masks */}
            <div
              className="absolute inset-0 pointer-events-none z-10"
              style={{
                maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                background: 'linear-gradient(to right, white 0%, transparent 8%, transparent 92%, white 100%)',
              }}
            />

            {/* Prev arrow */}
            <button
              onClick={handlePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-200 rounded-full shadow-md w-9 h-9 flex items-center justify-center hover:bg-gray-50 transition"
              aria-label="Previous brands"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            {/* Next arrow */}
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white border border-gray-200 rounded-full shadow-md w-9 h-9 flex items-center justify-center hover:bg-gray-50 transition"
              aria-label="Next brands"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* 4 rows */}
            <div className="flex flex-col gap-4 px-10 overflow-hidden">
              <BrandRow brands={rows[0]} direction={1}  isPaused={isDesktopPaused} rowRef={row1Ref} />
              <BrandRow brands={rows[1]} direction={-1} isPaused={isDesktopPaused} rowRef={row2Ref} />
              <BrandRow brands={rows[2]} direction={1}  isPaused={isDesktopPaused} rowRef={row3Ref} />
              <BrandRow brands={rows[3]} direction={-1} isPaused={isDesktopPaused} rowRef={row4Ref} />
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-500 text-center mt-6 mb-3">
          Scroll to explore &middot; Tap a brand to shop
        </p>

        {/* Mobile: single-row auto-scrolling ticker (shown below md) */}
        <div className="block md:hidden">
          <div
            className="relative"
            onTouchStart={() => {
              setIsMobilePaused(true);
              if (touchTimeoutRef.current) clearTimeout(touchTimeoutRef.current);
            }}
            onTouchEnd={() => {
              touchTimeoutRef.current = setTimeout(() => setIsMobilePaused(false), 2000);
            }}
          >
            {/* Fade mask for mobile */}
            <div
              className="absolute inset-0 pointer-events-none z-10"
              style={{
                maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
              }}
            />
            <BrandRow brands={brands} direction={1} isPaused={isMobilePaused} rowRef={mobRowRef} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandCarousel;
